'use client';

/**
 * Auth Context
 * Provides authentication state and user information
 *
 * Wraps next-auth/react's useSession for consistent access pattern
 */

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { useSession, signOut as nextAuthSignOut } from 'next-auth/react';

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  id: string; // Cognito sub
  email: string;
  name?: string | null;
  customerId?: number; // Local DB customer ID
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, status } = useSession();

  const user: AuthUser | null = session?.user
    ? {
        id: session.user.id,
        email: session.user.email!,
        name: session.user.name,
        customerId: session.user.customerId,
      }
    : null;

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!session?.user,
    isLoading: status === 'loading',
    signOut: async () => {
      // Clear cart session so next user doesn't see previous user's cart
      localStorage.removeItem('cart_session_id');
      await nextAuthSignOut({ callbackUrl: '/' });
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
